import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import api from "@/lib/api";
import { SITE } from "@/data/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [all, setAll] = useState([]);
  const [state, setState] = useState("loading");
  useEffect(() => {
    setState("loading");
    api.get(`/blog/${slug}`).then((r) => { setPost(r.data); setState("ok"); }).catch(() => setState("error"));
    api.get(`/blog`).then((r) => setAll(r.data || [])).catch(() => {});
  }, [slug]);

  if (state === "error") return <Navigate to="/blog" replace />;
  if (state === "loading") return <div className="mx-auto max-w-3xl px-6 py-32 font-sans text-sm text-muted-foreground">Loading…</div>;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.coverImage,
    datePublished: post.publishedAt,
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: SITE.name },
    mainEntityOfPage: `${SITE.domain}/blog/${post.slug}`,
  };

  const primaryTag = post.tags?.[0];
  const related = all
    .filter((p) => p.slug !== post.slug)
    .sort((a, b) => (b.tags?.includes(primaryTag) ? 1 : 0) - (a.tags?.includes(primaryTag) ? 1 : 0))
    .slice(0, 3);

  return (
    <>
      <Seo title={`${post.title} | Let Go Wellness`} description={post.excerpt} path={`/blog/${post.slug}`} image={post.coverImage} jsonLd={schema} />
      <article className="border-b border-border">
        <div className="mx-auto max-w-3xl px-6 pt-16">
          <Link to="/blog" className="inline-flex items-center gap-2 font-sans text-sm text-clay hover:underline" data-testid="blog-back"><ArrowLeft size={14} /> The Journal</Link>
          <div className="overline mt-8 mb-4">{post.tags?.join(" · ")} · {post.readMinutes} min read</div>
          <h1 className="font-serif text-4xl leading-tight text-ink sm:text-5xl">{post.title}</h1>
          <p className="mt-5 font-sans text-sm text-muted-foreground">By {post.author} · {new Date(post.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
        <div className="mx-auto mt-12 max-w-4xl px-6">
          <div className="aspect-[21/9] overflow-hidden"><img src={post.coverImage} alt={post.title} className="h-full w-full object-cover" /></div>
        </div>
        <div className="mx-auto max-w-3xl px-6 py-16">
          <div className="prose-editorial" data-testid="blog-body" dangerouslySetInnerHTML={{ __html: post.body }} />
        </div>
      </article>
      {related.length > 0 && (
        <section className="border-b border-border bg-white py-20" data-testid="related-reads">
          <div className="mx-auto max-w-6xl px-6">
            <div className="overline mb-8">Keep reading</div>
            <div className="grid gap-px overflow-hidden border border-border bg-border md:grid-cols-3">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-${p.slug}`} className="group flex flex-col bg-white transition-colors hover:bg-linen">
                  <div className="aspect-[3/2] overflow-hidden"><img src={p.coverImage} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /></div>
                  <div className="flex flex-1 flex-col p-8">
                    <div className="overline mb-3 flex items-center gap-2"><Clock size={12} />{p.readMinutes} min · {p.tags?.[0]}</div>
                    <h3 className="font-serif text-xl leading-tight text-ink">{p.title}</h3>
                    <span className="mt-6 inline-flex items-center gap-2 font-sans text-xs text-clay group-hover:translate-x-1">Read <ArrowRight size={13} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <NewsletterSignup />
      <CtaBand />
    </>
  );
}
